import React, {memo, RefObject, useCallback, useEffect, useId, useMemo, useRef, useState} from 'react'
import type {Widget} from '@dv5/domain'
import type {CanvasPosition} from '../model/CanvasPosition'
import {getCanvasScale} from '../containers/utils/canvasViewport'
import {getMinimapBounds, getWidgetBounds, minimapToCanvas, MinimapBounds} from '../containers/utils/canvasMinimap'
import './CanvasMinimap.less'

interface Props {
    widgets: Widget[]
    viewportRef: RefObject<HTMLDivElement | null>
    scrollPosition: CanvasPosition
    zoom: number
    onNavigate: (position: CanvasPosition) => void
}

const Silhouettes = memo(({widgets}: {widgets: Widget[]}) => <g className="canvas-minimap__widgets">
    {widgets.map(widget => <rect key={widget.id} x={widget.left} y={widget.top}
        width={widget.width} height={widget.height} vectorEffect="non-scaling-stroke"/>)}
</g>)

export const CanvasMinimap = ({widgets, viewportRef, scrollPosition, zoom, onNavigate}: Props) => {
    const helpId = useId()
    const [size, setSize] = useState({width: 0, height: 0})
    const [dragBounds, setDragBounds] = useState<MinimapBounds | null>(null)
    const drag = useRef<{pointerId: number, bounds: MinimapBounds, offset: CanvasPosition} | null>(null)
    const svgRef = useRef<SVGSVGElement | null>(null)
    const contentBounds = useMemo(() => getWidgetBounds(widgets), [widgets])
    const scale = getCanvasScale(zoom)
    const viewport = {...scrollPosition, width: size.width / scale, height: size.height / scale}
    const bounds = dragBounds ?? getMinimapBounds(contentBounds, viewport)

    useEffect(() => {
        const element = viewportRef.current
        if (!element) return
        const measure = () => setSize({width: element.clientWidth, height: element.clientHeight})
        measure()
        const observer = new ResizeObserver(measure)
        observer.observe(element)
        return () => observer.disconnect()
    }, [viewportRef])

    const stopDrag = useCallback(() => {
        const current = drag.current
        drag.current = null
        setDragBounds(null)
        if (current && svgRef.current?.hasPointerCapture(current.pointerId)) {
            svgRef.current.releasePointerCapture(current.pointerId)
        }
    }, [])
    useEffect(() => {
        window.addEventListener('blur', stopDrag)
        return () => window.removeEventListener('blur', stopDrag)
    }, [stopDrag])

    const getPosition = (event: React.PointerEvent<SVGSVGElement>, mapping: MinimapBounds) => {
        const svg = event.currentTarget
        const rect = svg.getBoundingClientRect()
        return minimapToCanvas({x: event.clientX - rect.left - svg.clientLeft, y: event.clientY - rect.top - svg.clientTop},
            {width: svg.clientWidth, height: svg.clientHeight}, mapping)
    }

    return <svg ref={svgRef} className={`canvas-minimap${dragBounds ? ' canvas-minimap--dragging' : ''}`}
        viewBox={`${bounds.left} ${bounds.top} ${bounds.width} ${bounds.height}`}
        role="group" aria-label="Canvas minimap" aria-describedby={helpId} tabIndex={0}
        onMouseDown={event => event.stopPropagation()}
        onClick={event => event.stopPropagation()}
        onWheel={event => event.stopPropagation()}
        onPointerDown={event => {
            event.stopPropagation()
            if (event.button !== 0 || drag.current) return
            event.preventDefault()
            event.currentTarget.focus()
            const point = getPosition(event, bounds)
            const insideViewport = point.left >= viewport.left && point.left <= viewport.left + viewport.width
                && point.top >= viewport.top && point.top <= viewport.top + viewport.height
            const offset = insideViewport
                ? {left: point.left - viewport.left, top: point.top - viewport.top}
                : {left: viewport.width / 2, top: viewport.height / 2}
            drag.current = {pointerId: event.pointerId, bounds, offset}
            setDragBounds(bounds)
            event.currentTarget.setPointerCapture(event.pointerId)
            if (!insideViewport) onNavigate({left: point.left - offset.left, top: point.top - offset.top})
        }}
        onPointerMove={event => {
            const current = drag.current
            if (!current || current.pointerId !== event.pointerId) return
            event.stopPropagation()
            const point = getPosition(event, current.bounds)
            onNavigate({left: point.left - current.offset.left, top: point.top - current.offset.top})
        }}
        onPointerUp={event => { if (drag.current?.pointerId === event.pointerId) stopDrag() }}
        onPointerCancel={stopDrag} onLostPointerCapture={stopDrag}
        onKeyDown={event => {
            event.stopPropagation()
            const step = (event.shiftKey ? 100 : 20) / scale
            const delta = {ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step]}[event.key]
            if (delta) {
                event.preventDefault()
                onNavigate({left: scrollPosition.left + delta[0], top: scrollPosition.top + delta[1]})
            } else if (event.key === 'Escape') stopDrag()
        }}>
        <desc id={helpId}>Click to navigate, drag the viewport to pan, or use arrow keys. Hold Shift for larger steps.</desc>
        <Silhouettes widgets={widgets}/>
        <rect className="canvas-minimap__viewport" x={viewport.left} y={viewport.top}
            width={viewport.width} height={viewport.height} vectorEffect="non-scaling-stroke"/>
    </svg>
}
